import { Flex, Typography } from 'antd';
import invariant from 'tiny-invariant';
import { useTranslation } from 'translations/hooks';

import { useAppContext } from 'business/contextProviders/useAppContext';
import {
  isModuleEnabled,
  usePermissions,
} from 'business/contextProviders/usePermissions';
import { DefectsDetailDto } from 'generated/apiSchemas';
import { Module_Enum } from 'generated/graphql';
import { formatDate } from 'technical/format/date';

import { DefectDetails } from './report-details';
import styles from './segment-details.module.scss';
import { DefectType } from './types';

const { Text } = Typography;

const defectTypeOrder: Record<DefectType, number> = {
  [DefectType.Quarantine]: 0,
  [DefectType.Tunnel]: 1,
};

export const SegmentDetails = ({
  label,
  defects,
  mold,
  manufactureDate,
  serialNumber,
}: {
  label: string;
  defects: DefectsDetailDto[];
  mold: string | null;
  manufactureDate: string | null;
  serialNumber: string | null;
}) => {
  const { t } = useTranslation();
  const { currentConstructionSite } = useAppContext();
  invariant(currentConstructionSite, 'No construction site');

  const { isDataManager } = usePermissions();

  const showSegmentReferenceData = isModuleEnabled(
    isDataManager,
    currentConstructionSite,
    Module_Enum.QualityScan,
  );

  // Quarantine (segment scan) defects displayed before tunnel control
  // defects.
  const sortedDefects = [...defects].sort(
    (a, b) =>
      defectTypeOrder[a.defectType as DefectType] -
      defectTypeOrder[b.defectType as DefectType],
  );

  return (
    <Flex vertical gap={12}>
      <div className={styles.card}>
        <Flex gap={24}>
          <Flex vertical>
            <Text type="secondary">
              {t('qualityControl.ringComplianceReport.detail.body.segmentType')}
            </Text>
            <Text strong>{label}</Text>
          </Flex>
          {showSegmentReferenceData && (
            <>
              <Flex vertical>
                <Text type="secondary">
                  {t(
                    'qualityControl.ringComplianceReport.detail.body.serialNumber',
                  )}
                </Text>
                <Text strong>{serialNumber ?? '-'}</Text>
              </Flex>
              <Flex vertical>
                <Text type="secondary">
                  {t('qualityControl.ringComplianceReport.detail.body.mold')}
                </Text>
                <Text strong>{mold ?? '-'}</Text>
              </Flex>
              <Flex vertical>
                <Text type="secondary">
                  {t(
                    'qualityControl.ringComplianceReport.detail.body.manufactureDate',
                  )}
                </Text>
                <Text strong>{formatDate(manufactureDate)}</Text>
              </Flex>
            </>
          )}
        </Flex>
        {sortedDefects.map((defect) => (
          <DefectDetails key={defect.id} data={defect} />
        ))}
      </div>
    </Flex>
  );
};
